/**
 * ZWEI BILDSCHIRME, EINE BUEHNE (Rafael, 25.09.2026 10:22Z).
 *
 * Bildschirm 1 zeigt links den Guide, rechts den Kanzler-/Assistentenchat. Jede
 * Buehne, die dort angeklickt wird, geht auf Bildschirm 2 auf — im Vollbild,
 * ohne Guide-Spalte. Anlass: Kurt hat zwei Monitore; auf einem war fuer Guide
 * oben / Chat unten kein Platz.
 *
 * Die B1/B2-Grundlage (eigener Layout-Schluessel je Fenster, `screenClaim.ts`)
 * gab es schon; hier liegt nur die Kopplung: ein BroadcastChannel zwischen zwei
 * Fenstern desselben Ursprungs (Browser wie Electron), je Person getrennt.
 *
 * Nichts davon darf still scheitern: jede Buehne, die Bildschirm 1 hinueberreicht,
 * muss Bildschirm 2 quittieren. Ohne Quittung oeffnet Bildschirm 1 sie selbst und
 * sagt das sichtbar — eine Buehne, die „irgendwo" aufgeht, waere schlimmer als eine
 * auf dem falschen Bildschirm.
 *
 * Diese Datei ist bis auf `oeffneKanal` rein und wird ohne DOM getestet.
 */

/** Lebenszeichen alle 2 s; nach 7 s ohne gilt die Gegenseite als weg. */
export const LEBENSZEICHEN_MS = 2000;
export const LEBENSZEICHEN_FRIST_MS = 7000;
/** So lange wartet Bildschirm 1 auf die Quittung, bevor er selbst oeffnet. */
export const QUITTUNG_FRIST_MS = 2500;
/**
 * So lange gibt Bildschirm 1 einem frisch geoeffneten (oder neu ladenden) Bildschirm 2
 * Zeit, sich zu melden — ein neues Fenster laedt die ganze App, das dauert laenger als
 * ein ausgebliebener Herzschlag.
 */
export const ANLAUF_FRIST_MS = 15_000;

/** URL-Parameter und sessionStorage-Merker des Buehnen-Vollbilds (Bildschirm 2). */
export const VOLLBILD_PARAMETER = 'bildschirm';
export const VOLLBILD_WERT = 'buehnen';
export const VOLLBILD_MERKER = 'cui-b2-buehnen-vollbild';
/** sessionStorage-Merker auf Bildschirm 1: er steuert einen zweiten Bildschirm. */
export const STEUER_MERKER = 'cui-b1-steuert-b2';

export type BildschirmNachricht =
  | { art: 'b1-lebt'; fenster: string; ts: number }
  | { art: 'b2-lebt'; fenster: string; ts: number }
  | { art: 'weg'; fenster: string; rolle: 'b1' | 'b2' }
  | { art: 'buehne'; nr: number; projectId: string; doc: unknown; bezug?: { id: string; textblock: string } }
  | { art: 'leeren'; nr: number }
  | { art: 'arbeitsbereich'; nr: number; projectId: string; panels: string[] }
  | { art: 'quittung'; nr: number; ok: boolean; fehler?: string };

export type AuftragsNachricht = Extract<BildschirmNachricht, { art: 'buehne' | 'leeren' | 'arbeitsbereich' }>;

const istText = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 500;
const istNr = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0;

/**
 * Prueft eine eingehende Nachricht. Unbekanntes ergibt `null` — der Aufrufer
 * protokolliert es; ein fremder Absender auf demselben Kanal darf nichts ausloesen.
 */
export function pruefeNachricht(roh: unknown): BildschirmNachricht | null {
  if (!roh || typeof roh !== 'object') return null;
  const n = roh as Record<string, unknown>;
  switch (n.art) {
    case 'b1-lebt': case 'b2-lebt':
      return istText(n.fenster) && typeof n.ts === 'number' ? { art: n.art, fenster: n.fenster, ts: n.ts } : null;
    case 'weg':
      return istText(n.fenster) && (n.rolle === 'b1' || n.rolle === 'b2') ? { art: 'weg', fenster: n.fenster, rolle: n.rolle } : null;
    case 'buehne': {
      if (!istNr(n.nr) || !istText(n.projectId) || !n.doc || typeof n.doc !== 'object') return null;
      const b = n.bezug as Record<string, unknown> | undefined;
      if (b !== undefined && (!b || typeof b.id !== 'string' || typeof b.textblock !== 'string')) return null;
      return { art: 'buehne', nr: n.nr, projectId: n.projectId, doc: n.doc, ...(b ? { bezug: { id: b.id as string, textblock: b.textblock as string } } : {}) };
    }
    case 'leeren':
      return istNr(n.nr) ? { art: 'leeren', nr: n.nr } : null;
    case 'arbeitsbereich':
      return istNr(n.nr) && istText(n.projectId) && Array.isArray(n.panels) && n.panels.every(istText)
        ? { art: 'arbeitsbereich', nr: n.nr, projectId: n.projectId, panels: n.panels as string[] } : null;
    case 'quittung':
      return istNr(n.nr) && typeof n.ok === 'boolean' && (n.fehler === undefined || typeof n.fehler === 'string')
        ? { art: 'quittung', nr: n.nr, ok: n.ok, ...(n.fehler !== undefined ? { fehler: n.fehler as string } : {}) } : null;
    default:
      return null;
  }
}

/** Lebt die Gegenseite? `null` heisst: nie gehoert. */
export function gegenseiteLebt(zuletzt: number | null, jetzt: number, frist = LEBENSZEICHEN_FRIST_MS): boolean {
  return zuletzt !== null && jetzt - zuletzt <= frist;
}

/**
 * IST DIE GEGENSEITE ENDGUELTIG WEG? (Rafael, 25.09.2026: „Jetzt sieht man in der
 * Ansicht nirgendwo mehr dieses Fenster, wo er Bildschirme aufteilen kann … Prüf das
 * alles noch mal durch, sodass das gut zugänglich ist.")
 *
 * Gemessen bei Kurt (Firefox, 25.09.2026): ein Buehnen-Vollbild ohne Bildschirm 1
 * blieb fuer immer Vollbild, und Bildschirm 1 zeigte nach dem Schliessen von
 * Bildschirm 2 fuer immer „getrennt". Beide Fenster warteten auf eine Gegenseite,
 * die nie wiederkam. Ab jetzt gibt jede Seite nach einer Anlaufzeit auf und kehrt in
 * den Normalzustand zurueck.
 *
 * `anlaufSeit` ist der Beginn der Kopplung ODER der Zeitpunkt, an dem die Gegenseite
 * „weg" gemeldet hat: ein Neuladen der Gegenseite schickt ebenfalls „weg" und meldet
 * sich Sekunden spaeter wieder — das darf die Kopplung nicht zerreissen.
 */
export function gegenseiteEndgueltigWeg(
  anlaufSeit: number, zuletzt: number | null, jetzt: number,
  anlaufFrist = LEBENSZEICHEN_FRIST_MS, lebensFrist = LEBENSZEICHEN_FRIST_MS,
): boolean {
  if (gegenseiteLebt(zuletzt, jetzt, lebensFrist)) return false;
  return jetzt - anlaufSeit > anlaufFrist;
}

/**
 * Buch der offenen Auftraege auf Bildschirm 1: was hinuebergegangen ist und noch
 * keine Quittung hat. Rein — die Uhr kommt von aussen.
 */
export interface OffenerAuftrag { nachricht: AuftragsNachricht; gesendet: number }
export class AuftragsBuch {
  private naechsteNr = 1;
  private offen = new Map<number, OffenerAuftrag>();

  /** Vergibt die Nummer und merkt den Auftrag. */
  eintragen<T extends Omit<AuftragsNachricht, 'nr'>>(ohneNr: T, jetzt: number): AuftragsNachricht {
    const nachricht = { ...ohneNr, nr: this.naechsteNr++ } as AuftragsNachricht;
    this.offen.set(nachricht.nr, { nachricht, gesendet: jetzt });
    return nachricht;
  }

  /** Quittung verbuchen. Liefert den Auftrag, oder `null` fuer eine unbekannte/doppelte Quittung. */
  quittieren(nr: number): OffenerAuftrag | null {
    const a = this.offen.get(nr) ?? null;
    this.offen.delete(nr);
    return a;
  }

  /** Alle Auftraege, deren Frist abgelaufen ist — sie werden dabei ausgetragen. */
  abgelaufen(jetzt: number, frist = QUITTUNG_FRIST_MS): OffenerAuftrag[] {
    const weg: OffenerAuftrag[] = [];
    for (const [nr, a] of this.offen) if (jetzt - a.gesendet > frist) { weg.push(a); this.offen.delete(nr); }
    return weg;
  }

  /** Alles Offene austragen (Gegenseite ist weg) — der Aufrufer oeffnet es selbst. */
  alleAustragen(): OffenerAuftrag[] {
    const alle = [...this.offen.values()];
    this.offen.clear();
    return alle;
  }

  get anzahl(): number { return this.offen.size; }
}

/** Kanalname je Person — zwei Anmeldungen im selben Browser sehen einander nicht. */
export function kanalName(personKey: string): string {
  if (!personKey) throw new Error('[zweiterBildschirm] Kanal ohne Person');
  return `cui-zweiter-bildschirm:${personKey}`;
}

export interface Kanal { senden(n: BildschirmNachricht): void; schliessen(): void }

/**
 * Oeffnet den Kanal. Ohne BroadcastChannel (sehr alter Browser) wird geworfen —
 * der Aufrufer zeigt das am Knopf, statt eine Kopplung vorzutaeuschen.
 */
export function oeffneKanal(personKey: string, beiNachricht: (n: BildschirmNachricht) => void): Kanal {
  if (typeof BroadcastChannel === 'undefined') {
    throw new Error('Dieser Browser kann keine zwei Fenster koppeln (BroadcastChannel fehlt).');
  }
  const kanal = new BroadcastChannel(kanalName(personKey));
  kanal.onmessage = (e: MessageEvent) => {
    const n = pruefeNachricht(e.data);
    if (!n) { console.error('[zweiterBildschirm] unbekannte Nachricht verworfen:', e.data); return; }
    beiNachricht(n);
  };
  return {
    senden: n => kanal.postMessage(n),
    schliessen: () => kanal.close(),
  };
}

export function fensterKennung(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
